import { useDeviceQuery } from "../query/deviceQuery";
import { useTimeZonesQuery } from "../query/timeZonesQuery";
import { useMemo, useState } from "react";
import { checkmark, close, phonePortraitOutline } from "ionicons/icons";
import {
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonTitle,
  IonSearchbar,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonPage,
  IonIcon,
} from "@ionic/react";
import { useDeviceTimeZoneMutation } from "../query/deviceTimeZoneMutation";
import useUX from '../utils/ux';
import { useTranslation } from '../hooks/useTranslation';
import { LoadingStateContainer } from '../components/StateContainer';
import styles from './DeviceTimeZoneModal.module.css';

interface ZoneInfo {
  timeZone: string;
  region: string | null;
  city: string;
  time?: string;
  offset?: string;
}

const parseZone = (timeZone: string) => {
  const slash = timeZone.indexOf('/');
  return slash === -1
    ? { region: null, city: timeZone }
    : { region: timeZone.slice(0, slash), city: timeZone.slice(slash + 1).replace(/_/g, ' ') };
};

const zoneClock = (timeZone: string, now: Date) => {
  // The gateway zone list can contain names this runtime's ICU doesn't know.
  try {
    return {
      time: now.toLocaleTimeString(undefined, { timeZone, hour: '2-digit', minute: '2-digit' }),
      offset: new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' })
        .formatToParts(now).find((part) => part.type === 'timeZoneName')?.value,
    };
  } catch {
    return {};
  }
};

const phoneTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

const ZoneRow = ({ zone, selected, suggested, disabled, onSelect, phoneZoneNote }: {
  zone: ZoneInfo,
  selected?: boolean,
  suggested?: boolean,
  disabled?: boolean,
  onSelect: () => void,
  phoneZoneNote?: string,
}) => {
  // The suggested row swaps the region for the phone note to keep one line.
  const subtitle = [suggested ? undefined : zone.region, zone.offset, suggested ? phoneZoneNote : undefined]
    .filter(Boolean).join(' · ');
  return (
    <IonItem button detail={false} disabled={disabled} onClick={onSelect}
      className={selected ? styles.rowSelected : undefined}>
      {suggested && (
        <span slot="start" className={styles.rowIcon} aria-hidden="true">
          <IonIcon icon={phonePortraitOutline} />
        </span>
      )}
      <IonLabel>
        <h2 className={styles.rowTitle}>{zone.city}</h2>
        {subtitle && <p className={suggested ? styles.suggestedNote : undefined}>{subtitle}</p>}
      </IonLabel>
      {zone.time && <span slot="end" className={styles.rowTime}>{zone.time}</span>}
      {selected && <IonIcon slot="end" color="primary" icon={checkmark} className={styles.rowCheck} />}
    </IonItem>
  );
};

const DeviceTimeZoneModal = ({ dismiss, deviceId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceId: string }) => {
  const { t } = useTranslation('modals');
  const timeZonesQuery = useTimeZonesQuery();
  const [searchQuery, setSearchQuery] = useState('');
  const deviceQuery = useDeviceQuery(deviceId);
  const deviceTimeZone = deviceQuery.data?.timeZone;
  const mutation = useDeviceTimeZoneMutation(deviceId);
  const { presentResult } = useUX();

  const saveTimeZone = async (timeZone: string) => {
    presentResult(async () => {
        await mutation.mutateAsync(timeZone);
        dismiss(timeZone, 'select');
    }, t('device_timezone.updated'));
  };

  const zones = useMemo<ZoneInfo[]>(() => {
    const now = new Date();
    return (timeZonesQuery.data ?? []).map((tz) => ({
      timeZone: tz.timeZone,
      ...parseZone(tz.timeZone),
      ...zoneClock(tz.timeZone, now),
    }));
  }, [timeZonesQuery.data]);

  const query = searchQuery.trim().toLowerCase();
  const filteredZones = query
    ? zones.filter((z) => z.timeZone.toLowerCase().includes(query) || z.city.toLowerCase().includes(query))
    : zones;

  const regionGroups = useMemo(() => {
    const groups = new Map<string | null, ZoneInfo[]>();
    for (const zone of filteredZones) {
      const group = groups.get(zone.region);
      if (group) group.push(zone);
      else groups.set(zone.region, [zone]);
    }
    // Alphabetical regions, single-name zones (e.g. UTC) grouped last.
    return [...groups.entries()]
      .sort(([a], [b]) => a === null ? 1 : b === null ? -1 : a.localeCompare(b))
      .map(([region, regionZones]) => ({ region, zones: regionZones }));
  }, [filteredZones]);

  const currentZone = deviceTimeZone ? zones.find((z) => z.timeZone === deviceTimeZone) : undefined;
  const phoneZone = phoneTimeZone();
  const suggestedZone = phoneZone && phoneZone !== deviceTimeZone
    ? zones.find((z) => z.timeZone === phoneZone)
    : undefined;

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('device_timezone.title')}</IonTitle>
          <IonButtons slot="primary">
            <IonButton onClick={() => dismiss(null, 'cancel')}>
              <IonIcon slot="icon-only" icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            className={styles.searchbar}
            value={searchQuery}
            placeholder={t('device_timezone.search_placeholder')}
            onIonInput={(e: any) => setSearchQuery(e.target.value)} />
        </IonToolbar>
      </IonHeader>

      <IonContent color="light">
        {timeZonesQuery.isPending ? <LoadingStateContainer /> : (
          <div className={styles.wrapper}>
            {!query && currentZone && (
              <section className={styles.section}>
                <h3 className={styles.sectionLabel}>{t('device_timezone.section_current')}</h3>
                <IonList className={styles.listCard}>
                  <ZoneRow zone={currentZone} selected disabled={mutation.isPending}
                    onSelect={() => saveTimeZone(currentZone.timeZone)} />
                </IonList>
              </section>
            )}
            {!query && suggestedZone && (
              <section className={styles.section}>
                <h3 className={styles.sectionLabel}>
                  {t('device_timezone.section_suggested')}
                  <span className={styles.sectionLabelNote}>· {t('device_timezone.suggested_from_phone')}</span>
                </h3>
                <IonList className={styles.listCard}>
                  <ZoneRow zone={suggestedZone} suggested disabled={mutation.isPending}
                    phoneZoneNote={t('device_timezone.phone_zone_note')}
                    onSelect={() => saveTimeZone(suggestedZone.timeZone)} />
                </IonList>
              </section>
            )}
            {regionGroups.map((group) => (
              <section key={group.region ?? 'other'} className={styles.section}>
                <h3 className={styles.sectionLabel}>{group.region ?? t('device_timezone.region_other')}</h3>
                <IonList className={styles.listCard}>
                  {group.zones.map((zone) => (
                    <ZoneRow key={zone.timeZone} zone={zone}
                      selected={zone.timeZone === deviceTimeZone}
                      disabled={mutation.isPending}
                      onSelect={() => saveTimeZone(zone.timeZone)} />
                  ))}
                </IonList>
              </section>
            ))}
            {query && filteredZones.length === 0 && (
              <p className={styles.note}>{t('device_timezone.no_results')}</p>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
}

export default DeviceTimeZoneModal;
